import React, { useEffect, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from '@mui/material/Divider';
import * as api from '../api';
import * as analytics from '../analytics';

function CitySelector({ country, selectedCities, onSelectCities }) {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const loadCities = async () => {
      if (country) {
        setLoading(true);
        setError(null);
        try {
          const data = await api.fetchCities();
          const filtered = data.filter(city => String(city.country_id) === String(country));
          setCities(filtered);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };

    loadCities();
  }, [country]);

  if (loading) return <Typography>Loading cities...</Typography>;
  if (error) return <Typography color="error">Error: {error}</Typography>;

  const handleSelectChange = (e) => {
    const selectedIds = e.target.value;
    const selectedCityNames = selectedIds.map(id => {
      const city = cities.find(c => c.id === id);
      return city?.name;
    }).filter(Boolean);
    analytics.trackCitiesSelected(selectedIds.length, selectedCityNames);
    onSelectCities(selectedIds);
    // Close the dropdown after selection
    setOpen(false);
  };

  const handleDelete = (cityId) => {
    onSelectCities(selectedCities.filter(id => id !== cityId));
  };

  const handleSelectAll = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const allCityIds = cities.map(city => city.id);
    onSelectCities(allCityIds);
    analytics.trackSelectAllCities(allCityIds.length);
    setOpen(false);
  };

  const handleClearAll = () => {
    onSelectCities([]);
  };

  // Filter out already selected cities from the dropdown options
  const availableCities = cities.filter(city => !selectedCities.includes(city.id));
  const allSelected = cities.length > 0 && selectedCities.length === cities.length;

  return (
    <Box>
      {/* Selected cities chips - positioned above the dropdown */}
      {selectedCities.length > 0 && (
        <Box mb={2} display="flex" flexWrap="wrap" gap={1} alignItems="center">
          {selectedCities.map(id => {
            const city = cities.find(c => c.id === id);
            return city ? (
              <Chip
                key={id}
                label={city.name}
                onDelete={() => handleDelete(id)}
                deleteIcon={<CancelIcon />}
                color="primary"
                variant="outlined"
              />
            ) : null;
          })}
          {selectedCities.length > 1 && (
            <Chip
              label="Clear All"
              onClick={handleClearAll}
              color="error"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      )}
      
      <FormControl fullWidth>
        <InputLabel id="city-label">Cities</InputLabel>
        <Select
          labelId="city-label"
          multiple
          value={selectedCities}
          label="Cities"
          onChange={handleSelectChange}
          renderValue={() => ''}
          displayEmpty
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
        >
          {[
            ...(!allSelected && cities.length > 1 ? [
              <MenuItem key="select-all" onMouseDown={handleSelectAll} sx={{ color: 'primary.main', fontWeight: 500 }}>
                <ListItemIcon>
                  <SelectAllIcon fontSize="small" color="primary" />
                </ListItemIcon>
                Select All Cities
              </MenuItem>,
              <Divider key="divider" />
            ] : []),
            ...(availableCities.length === 0 ? [
              <MenuItem key="all-selected" disabled>
                <Typography color="text.secondary">All cities selected</Typography>
              </MenuItem>
            ] : availableCities.map(city => (
              <MenuItem key={city.id} value={city.id}>
                <ListItemIcon>
                  <LocationCityIcon fontSize="small" />
                </ListItemIcon>
                {city.name}
              </MenuItem>
            )))
          ]}
        </Select>
      </FormControl>
    </Box>
  );
}

export default CitySelector; 